<template>
    <teleport to="body">
            
        <div id="modal-dataset-preview" class="flex flex-col items-center p-10 fixed inset-0">

            <div id="dataset-preview-header" class="bg-white w-60 rounded-t-lg" style="min-width: 1200px;">
                
                <div class="flex flex-row justify-between items-center px-10 py-4">

                    <div class="flex items-center">
                        <div class="w-16 h-16 image-fit mr-1 flex-shrink-0">
                            <img
                                :src="layout.dataset?.preview || require('@/assets/images/rosepetal/icon/logoLoadingGray.png')"
                                class="w-full h-full rounded-full zoom-in cursor-default border border-gray-300"
                            />
                        </div>

                        <div v-if="layout.dataset?.name" class="ml-3 text-left mt-0 flex-shrink-0">
                            <a class="font-medium text-base cursor-pointer" @click="showEditDataset = true;">{{ layout.dataset?.name.slice(0, 35) }}<span v-if="(layout.dataset.name.length>35)">...</span><Edit-3Icon class="w-4 h-4" /></a>
                            <div>
                                <span v-if="layout.dataset.type" class="text-gray-600 text-xs mt-0.5">{{ layout.dataset.typeName }}</span>
                            </div>
                            <span v-if="layout.dataset.createdDate" class="text-gray-600 mt-0.5 font-italic" style="font-size:10px">{{ layout.dataset.createdDate }}</span>
                        </div>
                    </div>

                    <div class="flex items-center space-x-1">
                        <button id="mdTabOverview" type="button" class="tab-button px-5 py-2" 
                            :class="layout.tab === 'overview' ? 'tab-button-active' : ''"
                            @click="layout.tab = 'overview'">
                            <EyeIcon class="tab-icon"/>
                            <span class="tab-text">{{ $t('Overview') }}</span>
                        </button>

                        <button v-if="layout.dataset" id="rpBtnLabeling" class="tab-button px-5 py-2" @click="showLabeling=true">
                            <ImageIcon class="tab-icon" />
                            <span class="tab-text">{{$t('Labeling')}}</span>
                        </button>
                        
                        <button id="mdTabTags" type="button" class="tab-button px-5 py-2" 
                            :class="layout.tab === 'tags' ? 'tab-button-active' : ''"
                            @click="layout.tab = 'tags'">
                            <TagIcon class="tab-icon"/>
                            <span class="tab-text">{{ $t('Tags') }}</span>
                        </button>

                        <button id="mdTabModels" type="button" class="tab-button px-5 py-2" 
                            :class="layout.tab === 'models' ? 'tab-button-active' : ''"
                            @click="layout.tab = 'models'">
                            <CpuIcon class="tab-icon"/>
                            <span class="tab-text">{{ $t('Models') }}</span>
                        </button>

                        <button id="mdTabUpload" type="button" class="tab-button px-5 py-2" 
                            :class="layout.tab === 'upload' ? 'tab-button-active' : ''"
                            @click="layout.tab = 'upload'">
                            <UploadIcon class="tab-icon"/>
                            <span class="tab-text">{{ $t('Upload') }}</span>
                        </button>

                        <button v-if="layout.dataset.type === 'imageObjectDetection'" id="mdTabValidate" type="button" class="tab-button px-5 py-2" 
                            :class="layout.tab === 'validate' ? 'tab-button-active' : ''"
                            @click="layout.tab = 'validate'">
                            <CheckSquareIcon class="tab-icon"/>
                            <span class="tab-text">{{ $t('Validate') }}</span>
                        </button>
                    </div>

                    <div class="flex justify-end items-center space-x-1 flex-shrink-0">
                        <div id="rpBtnPrevDataset"
                            :class="{
                                'btn bg-gray-100 zoom-in border py-1.5 px-3 text-gray-600': true,
                                'opacity-50 cursor-not-allowed': !prevDataset
                            }" 
                            title="Previous dataset" 
                            @click="navigateDataset('prev')">
                            <Corner-up-leftIcon class="w-5 h-5" />
                        </div>

                        <div id="rpBtnNextDataset"
                            :class="{
                                'btn bg-gray-100 zoom-in border py-1.5 px-3 text-gray-600': true,
                                'opacity-50 cursor-not-allowed': !nextDataset
                            }" 
                            title="Next dataset" 
                            @click="navigateDataset('next')">
                            <Corner-up-rightIcon class="w-5 h-5" />
                        </div>

                        <div v-if="layout.dataset" id="rpBtnClosePreview" class="btn bg-gray-100 zoom-in border py-1.5 px-3 text-gray-600" title="Home" @click="$emit('closeModal')">
                            <HomeIcon class="w-5 h-5"/>
                        </div>

                        <div class="relative" ref="dropdownRef">
                            <div id="rpBtnMoreOptions" class="btn bg-gray-100 zoom-in border py-1.5 px-3 text-gray-600" title="More options" @click="toggleDropdown">
                                <MoreHorizontalIcon class="w-5 h-5" />
                            </div>
                            
                            <div v-if="showDropdown" class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border z-50">
                                <div class="py-1">
                                    <a v-if="layout.dataset" @click="reload(); closeDropdown()" class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                                        <Refresh-cwIcon class="w-4 h-4 mr-3" />
                                        {{ $t('Reload') }}
                                    </a>
                                    <a v-if="layout.dataset" @click="showDownloadDataset = true; closeDropdown()" class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                                        <DownloadIcon class="w-4 h-4 mr-3" />
                                        {{ $t('Download') }}
                                    </a>
                                    <a @click="layout.tab = 'copy'; closeDropdown()" class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                                        <CopyIcon class="w-4 h-4 mr-3" />
                                        {{ $t('Copy') }}
                                    </a>
                                    <div class="border-t border-gray-100"></div>
                                    <a @click="showDeleteDataset = true; closeDropdown()" class="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer">
                                        <TrashIcon class="w-4 h-4 mr-3" />
                                        {{ $t('Delete') }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
            
            <div id="dataset-preview-body" v-if="layout.dataset" class="flex flex-col flex-1 w-60 bg-white rounded-b-lg overflow-y-auto" style="min-width: 1200px; margin-top: -1px;">

                <div v-if="!layout.loading" class="flex flex-col flex-1 pt-0">

                    <div class="tab-section" v-if="layout.tab === 'overview'">
                        <OverviewCmp 
                            :dataset="layout.dataset" 
                            :datasetsList="datasetsList" 
                            @updateTab="layout.tab = $event"
                            @editImage="manageEditor($event)"
                            @showEditDatasetModal="showEditDataset = true"/> 
                    </div>

                    <div class="tab-section" v-if="layout.tab === 'tags'">
                        <TagsCmp 
                            :dataset="layout.dataset" 
                            @updateDataset="updateDataset($event)"/>
                    </div>

                    <div class="tab-section" v-if="layout.tab === 'models'">
                        <ModelsCmp 
                            :dataset="layout.dataset"
                            @updateDataset="updateDataset($event)"
                            @switchToValidation="layout.tab = 'validate'"/>
                    </div>

                    <div class="tab-section" v-if="layout.tab === 'upload'">
                        <DropzoneCmp 
                            :datasetId="layout.dataset.id" 
                            @updateDataset="updateDataset($event)"
                            @closeDropzone="layout.tab='overview'"/>
                    </div>

                    <div class="tab-section" v-if="layout.tab === 'validate'">
                        <ValidatorCmp 
                            :dataset="layout.dataset" 
                            :datasetsList="datasetsList" 
                            @updateTab="layout.tab = $event"
                            @updateDataset="updateDataset($event)"
                            @closeModal="showValidator = false" />
                    </div>

                    <div class="tab-section" v-if="layout.tab === 'copy'">
                        <CopyCmp 
                            :dataset="layout.dataset" />
                    </div>

                </div>

                <div v-else class="flex flex-col justify-center items-center pb-10">
                    <div class="flex items-center justify-center mt-10">
                        <img :src="require(`@/assets/images/rosepetal/icon/logoLoadingGray.png`)" class="w-20" />
                    </div>
                    <LoadingIcon icon="three-dots" class="w-8 h-8" />
                    <p class="text-sm">{{ $t('Loading dataset...') }}</p>
                </div>

            </div>
            
        </div>

        <!-- Labeling -->
        <div v-if="showLabeling">
            <LabelingCmp 
                :options="{ 
                    'dataset': 
                    layout.dataset, 'datasetsList': {} 
                }" 
                @closeLabeling="manageDataset(layout.dataset); showLabeling = false"/>
        </div>

        <!-- Tools -->
        <div v-if="showTools">
            <ToolsCmp 
                :options="{ 
                    'dataset': layout.dataset.id, 
                    'datasetsList': {} 
                }"
                @closeTools="showTools = false"/>
        </div>

        <!-- Editor -->
        <div v-if="showEditor">
            <EditorCmp 
                :options="{ 
					'id': layout.selectedId, 
					'dataset': layout.dataset, 
					'datasetsList': layout.datasetsList, 
					'filters': layout.mosaicFilters
				}"
                @imageDeleted="showEditor = false; updateDataset('images')"
                @closeEditor="showEditor = false"/>
        </div>

        <!-- Edit dataset -->
        <div v-if="showEditDataset">
			<EditDatasetCmp 
				:dataset="layout.dataset"
				@closeModal="showEditDataset = false;"
				@datasetEdited="showEditDataset = false; manageDataset()"/>
		</div>

        <!-- Delete dataset -->
		<div v-if="showDeleteDataset">
			<DeleteDatasetCmp 
				:dataset="layout.dataset"
				@closeModal="showDeleteDataset = false;"
				@datasetDeleted="$emit('datasetDeleted')"/>
		</div>

        <!-- Download dataset -->
		<div v-if="showDownloadDataset">
			<DownloadDatasetCmp 
				:dataset="layout.dataset"
				@closeModal="showDownloadDataset = false;"/>
		</div>

    </teleport>
</template>

<script setup>
import { onMounted, computed, ref } from "vue";
import { useRouter }                from "vue-router";
import { useStore } 	            from "@/store";
import * as rosepetalModel  from "rosepetal-model";

import EditorCmp		    from "@/components/manage-image/Editor.vue";
import LabelingCmp		    from "@/components/manage-dataset/Labeling.vue";

import OverviewCmp		    from "@/components/manage-dataset/Overview.vue";
import TagsCmp              from "@/components/manage-dataset/Tags.vue";
import ModelsCmp		    from "@/components/manage-dataset/Models.vue";
import DropzoneCmp          from "@/components/dropzone/Main.vue";
import ValidatorCmp	        from "@/components/manage-dataset/Validator.vue";
import CopyCmp		        from "@/components/manage-dataset/Copy.vue";
import ToolsCmp			    from "@/components/manage-dataset/Tools.vue";

import EditDatasetCmp       from "@/modals/dataset/EditDataset.vue";
import DeleteDatasetCmp     from "@/modals/dataset/DeleteDataset.vue";
import DownloadDatasetCmp 	from "@/modals/dataset/DownloadDataset.vue";

const $dataset = rosepetalModel.default.dataset;

const router    = useRouter();
const store     = useStore();
const User      = computed(() => store.state.main.User);

const nextDataset = computed(() => {
    const idx = layout.value.dataset.idx;
    const datasets = layout.value.datasetsList;
    if (typeof idx !== 'number' || !datasets?.length) return false;
    return idx < datasets.length - 1 ? datasets[idx + 1] : false;
});

const prevDataset = computed(() => {
    const idx = layout.value.dataset.idx;
    const datasets = layout.value.datasetsList;
    if (typeof idx !== 'number' || !datasets?.length) return false;
    return idx > 0 ? datasets[idx - 1] : false;
});

const showAdminTools = computed(() => {
    return User && User.role && User.role.role_id && User.role.role_id == '3';
});

const filteredTags = computed(() => {
    if (layout.value.dataset) {
		if (layout.value.dataset.type === 'MULTICLASS' || layout.value.dataset.type === 'imageObjectDetection') {
			return Object.entries(layout.value.dataset.tags).reduce((acc, [id, tag]) => {
                if (!tag?.unclassified) { acc[id] = tag; }
                return acc;
            }, {});
		} else if (layout.value.dataset.type === 'MULTILABEL') {
			return Object.entries(layout.value.dataset.tags).reduce((acc, [id, tag]) => {
				if (!tag?.anomaly && !tag?.normal) { acc[id] = tag; }
				return acc;
			}, {});
		}
    }
    return {};
});

const props = defineProps({
    dataset: {
        type: Number,
        default: 0
    },
    datasetsList: {
        type: Object,
        default: () => ({})
    },
    tab: {
        type: String,
        default: 'overview'
    }
});

const $emit = defineEmits(['datasetUpdated']);

const showValidator         = ref(false);
const showEditDataset       = ref(false);
const showDeleteDataset     = ref(false);
const showDownloadDataset   = ref(false);
const showDropdown          = ref(false);

const showEditor    = ref(false);
const showLabeling  = ref(false);
const showTools     = ref(false);

const mosaicRef = ref(null);
const dropdownRef = ref(null);

const layout = ref({
    loading:            false,
    dataset:            {},
    datasetsList:       [],
    mosaicFilters:      {},
    selectedId:         false,
    tab:                'overview'
});

const manageRouter = async() => {
    const urlSegments = window.location.pathname.split('/').filter(segment => segment);
    const [section, datasetID, action] = urlSegments;
    
    if (section === 'datasets' && datasetID) {
        if (action) {
            const newPath = `/datasets/${datasetID}/${action}`;
            switch (action) {
                case 'labeling':
                    showLabeling.value = true;
                    break;
                case 'tools':
                    showTools.value = true;
                    break;
                default:
                    layout.value.selectedId = action;
                    showEditor.value = true;
                    break;
            }
            router.push(newPath);
        } else {
            router.push(`/datasets/${datasetID}`);
        }
    } else { router.push(`/datasets/${props.dataset.id}`); }
}

const manageDataset = async () => {
    manageRouter();

    layout.value.tab = props.tab;
    layout.value.loading = true;

    if (!layout.value.dataset.id) {
        layout.value.dataset = props.dataset;
        layout.value.datasetsList = props.datasetsList;
    }

    const dsData = await $dataset.get(layout.value.dataset.id, { models: true, tagStats: true });
    sessionStorage.setItem('datasetData', JSON.stringify(dsData));
    console.log('Dataset data loaded:', dsData);

    Object.assign(layout.value.dataset, {
        models:			dsData.models,
        tags:			dsData.tags,
        tagStats:		dsData.tagStats
    });

    Object.assign(layout.value.dataset, {
        resumeTxt: 		await $dataset.generateResume(dsData),
        datasetStatus: 	'ready'
    });

    layout.value.loading = false;
}

const manageEditor = async ($event) => {
    layout.value.selectedId     = $event.imageId; 
    layout.value.mosaicFilters  = $event.filters; 
    showEditor.value            = true;
}

const navigateDataset = async (direction) => {
    let idx = layout.value.dataset.idx;
    const total = Object.keys(layout.value.datasetsList).length;

    switch (direction) {
        case 'prev':
            if (idx - 1 >= 0) {
                idx--;
            } else {
                return;
            }
            break;
        case 'next':
            if (idx + 1 < total) {
                idx++;
            } else {
                return;
            }
            break;
    }
    
    layout.value.dataset = layout.value.datasetsList[idx];
    layout.value.dataset.idx = idx;

    await router.push('/datasets/' + layout.value.datasetsList[idx].id);
    await manageDataset();
}

const updateDataset = async (data = false) => {
    switch (data) {
        case 'tags':
        case 'images':
            layout.value.dataset.tagStats = await $dataset.getTagStats(layout.value.dataset);
            break;
        case 'models':
            layout.value.dataset.models = await $dataset.getModels(layout.value.dataset);
            break;
        default:
            manageDataset(layout.value.dataset, false);
            break;
    }

    $emit('datasetUpdated', layout.value.dataset);
}

const reload = async () => {
    manageDataset();
    if (mosaicRef.value) {
        mosaicRef.value.loadMosaicImages();
    }
}

const downloadDataset = async () => {
    if (layout.value.dataset && layout.value.dataset.id) {
        try {
            const response = await $dataset.download(layout.value.dataset.id);
            if (response && response.downloadUrl) {
                const link = document.createElement('a');
                link.href = response.downloadUrl;
                link.download = `${layout.value.dataset.name || 'dataset'}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error('Error downloading dataset:', error);
        }
    }
}

const toggleDropdown = () => {
    showDropdown.value = !showDropdown.value;
}

const closeDropdown = () => {
    showDropdown.value = false;
}

onMounted(() => {
    manageDataset();
    
    document.addEventListener('click', (event) => {
        if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
            closeDropdown();
        }
    });
});
</script>

<style>
#modal-dataset-preview {
    top:        0;
    left:       0;
    width:      100%;
    height:     100%;
    z-index:    999;
    background-color: #f2f3f3;
}

.tab-button {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 0.25rem;
    border-radius: 0.375rem;
    background-color: #edf2fa;
    border: 1px solid #a7bfe4;
    cursor: pointer;
    color: #1e4591;   
}

.tab-button:hover,
.tab-button-active {
    display: flex;
    border-color: #17356e;
    background-color: #17356e;
    color: white;
    gap: 0.5rem;
}

.tab-icon {
    width: 1rem;
    height: 1rem;
    color: #1e4591;
}

.tab-button:hover .tab-icon,
.tab-button-active .tab-icon {
    color: white;
}

.tab-text {
    opacity: 0;
    max-width: 0;
    overflow: hidden;
    white-space: nowrap;
    transition: opacity 0.3s ease, max-width 0.5s ease;
}

.tab-button:hover .tab-text,
.tab-button-active .tab-text {
    opacity: 1;
    max-width: 200px;
}

.tab-button-danger {
  background-color: #fee2e2;
  color: #dc2626;
  border-color: #fecaca;
} 

.tab-button-danger:hover {
    border-color: #fecaca;
    background-color: #fecaca;
}

.tab-button-danger .tab-icon,
.tab-button-danger:hover .tab-icon,
.tab-button-danger .tab-text,
.tab-button-danger:hover .tab-text {
  color: #dc2626;
}

.tab-section {
    flex: 1 1 0%;
    --tw-bg-opacity: 1;
    background-color: rgba(255, 255, 255, var(--tw-bg-opacity));
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
}
</style>